import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import PeakEngine from '@/components/PeakEngine';
import { GoalPlanFlow } from './GoalPlanFlow';
import { localDay } from './GuidedDay';

const MODES = [
  ['metrics', 'Train for metrics', 'Pick one or more metrics, model each to the target day, then build one plan toward them.'],
  ['day', 'Simulate to a day', 'Choose the target day and statistic. Review the estimated changes before applying.'],
  ['goal', 'Train for a goal', 'Pick a broad goal such as speed and power or lower-body strength, then review two plans.'],
];

const useScopedAthletes = (athleteId, squad, academyId, skip) => {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (skip) return;
    let live = true;
    api.get('/athletes').then(r => { if (live) setList(r.data.filter(a => athleteId ? a.athlete_id === athleteId : (!academyId || a.academy_id === academyId) && [a.sub_team, ...(a.teams || []), a.sub_team ? null : a.sport].includes(squad))); }).catch(() => live && setList([]));
    return () => { live = false; };
  }, [athleteId, squad, academyId, skip]);
  return list;
};

export const SimulatorDialog = ({ open, onClose, athleteId = '', squad = '', academyId = '', date, athletes: given }) => {
  const [mode, setMode] = useState('metrics');
  const loaded = useScopedAthletes(athleteId, squad, academyId, !!given || !open);
  const athletes = given || loaded || [];
  const day = date || new URLSearchParams(window.location.search).get('date') || localDay();
  const scopeAcademy = academyId || athletes[0]?.academy_id || '';
  const done = () => { window.dispatchEvent(new Event('trifit-synced')); onClose(); };
  const desc = MODES.find(m => m[0] === mode)[2];
  return <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
    <DialogContent data-testid="simulator-dialog" className="!transform-none left-[2vw] top-[4vh] w-[96vw] max-w-none max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle data-testid="simulator-title">{squad ? `${squad} · Team simulator` : `${athletes[0]?.name || 'Athlete'} · Individual simulator`}</DialogTitle>
        <DialogDescription data-testid="simulator-description">{desc}{squad ? ' Team results use the team average.' : ''} Nothing changes until you approve.</DialogDescription>
      </DialogHeader>
      <div role="tablist" aria-label="Simulator type" data-testid="simulator-modes" className="flex flex-wrap gap-2 border-b border-border pb-4">
        {MODES.map(([k, label]) => <button key={k} role="tab" aria-selected={mode === k} data-testid={`simulator-mode-${k}`} onClick={() => setMode(k)}
          className={`px-4 py-2 text-sm rounded-full border transition-colors ${mode === k ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-surface'}`}>{label}</button>)}
      </div>
      {!athletes.length ? <p data-testid="simulator-loading" className="text-sm text-muted-foreground py-4">{loaded === null && !given ? 'Loading athletes…' : 'No athletes in this scope yet.'}</p>
        : mode === 'day' ? <PeakEngine key={`day:${athleteId || squad}`} athleteId={athleteId} squad={squad} academyId={scopeAcademy} squadMode={!!squad} targetDate={day > localDay() ? day : undefined} onApplied={done} guided />
          : <GoalPlanFlow key={mode} mode={mode} athletes={athletes} academyId={scopeAcademy} squad={squad} date={day} onDone={done} />}
    </DialogContent>
  </Dialog>;
};
